// src/pages/IntensiveResultsPage.js
// Results page for Pre-Marriage Intensive modules
// Visual Theme: Emerald/Teal with Gold accents

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { INTENSIVE_MODULE_DATA } from '../data/intensiveModules';
import { 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle, 
  TrendingUp,
  BookOpen,
  Heart,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import IntensiveTeachingPresentation from '../components/IntensiveTeachingPresentation';
import IntensivePrayerPresentation from '../components/IntensivePrayerPresentation';

const IntensiveResultsPage = () => {
  const { moduleNumber: moduleParam } = useParams();
  const moduleNumber = parseInt(moduleParam) || 1;
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [moduleData, setModuleData] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [couple, setCouple] = useState(null);
  const [partnerAssessment, setPartnerAssessment] = useState(null);
  const [resultsRevealed, setResultsRevealed] = useState(false);
  const [showTeaching, setShowTeaching] = useState(false);
  const [showPrayer, setShowPrayer] = useState(false);

  useEffect(() => {
    loadResults();
  }, [currentUser, moduleNumber]);

  const loadResults = async () => {
    try {
      setLoading(true);

      // Get user data
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        navigate('/couple/dashboard');
        return;
      }

      const userData = userDoc.data();

      // Get couple data
      const coupleDoc = await getDoc(doc(db, 'couples', userData.coupleId));
      if (!coupleDoc.exists()) {
        navigate('/couple/dashboard');
        return;
      }

      const coupleData = coupleDoc.data();
      setCouple(coupleData);

      // Check if results are revealed
      const permissions = coupleData.intensivePermissions || {};
      const isRevealed = permissions[moduleNumber]?.resultsRevealed === true;
      setResultsRevealed(isRevealed);

      // Get user's assessment
      const assessmentDoc = await getDoc(doc(db, 'assessments', currentUser.uid));
      if (assessmentDoc.exists()) {
        setAssessment(assessmentDoc.data());
      }

      // Get partner's assessment
      const partnerId = userData.partnerId;
      if (partnerId) {
        const partnerDoc = await getDoc(doc(db, 'assessments', partnerId));
        if (partnerDoc.exists()) {
          setPartnerAssessment(partnerDoc.data());
        }
      }

      // Get module data
      const module = INTENSIVE_MODULE_DATA[moduleNumber];
      if (!module) {
        navigate('/couple/dashboard');
        return;
      }

      setModuleData(module);
      setLoading(false);
    } catch (error) {
      console.error('Error loading results:', error);
      alert('Error loading results. Please try again.');
      navigate('/couple/dashboard');
    }
  };

  const getScoreInterpretation = (score) => {
    if (!moduleData?.scoringGuidance) return null;

    const { excellent, developing, vulnerable } = moduleData.scoringGuidance;

    if (score >= excellent.range[0] && score <= excellent.range[1]) {
      return { ...excellent, color: 'emerald', icon: '🌟' };
    }
    if (score >= developing.range[0] && score <= developing.range[1]) {
      return { ...developing, color: 'amber', icon: '📈' };
    }
    return { ...vulnerable, color: 'red', icon: '⚠️' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Loading results...</p>
        </div>
      </div>
    );
  }

  const myScore = assessment?.intensiveModuleScores?.[moduleNumber];
  const partnerScore = partnerAssessment?.intensiveModuleScores?.[moduleNumber];
  const maxScore = moduleData?.questions?.length * 5;
  const myCompleted = assessment?.completedIntensiveModules?.includes(moduleNumber);
  const partnerCompleted = partnerAssessment?.completedIntensiveModules?.includes(moduleNumber);
  const bothCompleted = myCompleted && partnerCompleted;

  const myInterpretation = myScore ? getScoreInterpretation(myScore) : null;
  const partnerInterpretation = partnerScore ? getScoreInterpretation(partnerScore) : null;

  return (
    <>
      {/* Teaching Modal */}
      {showTeaching && (
        <IntensiveTeachingPresentation
          moduleNumber={moduleNumber}
          onClose={() => setShowTeaching(false)}
        />
      )}

      {/* Prayer Modal */}
      {showPrayer && (
        <IntensivePrayerPresentation
          moduleNumber={moduleNumber}
          onClose={() => setShowPrayer(false)}
        />
      )}

      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-4 sm:p-6">
        <div className="max-w-4xl mx-auto py-6 sm:py-8">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/couple/dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>

            <div className="flex items-center gap-3 mb-2">
              <span className="text-3xl">{moduleData?.icon}</span>
              <span className="text-sm font-semibold text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full">
                Module {moduleNumber} of 10
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {moduleData?.name}
            </h1>
            <p className="text-gray-600 mt-1">{moduleData?.description}</p>
          </div>

          {/* Results Not Revealed Notice */}
          {!resultsRevealed && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <EyeOff className="w-8 h-8 text-amber-600 flex-shrink-0" />
                <div>
                  <h2 className="text-lg font-bold text-amber-900 mb-2">
                    Results Pending Counselor Review
                  </h2>
                  <p className="text-amber-800">
                    Your counselor will review your responses and reveal the detailed results 
                    during your next session. In the meantime, you can access the teaching 
                    materials and prayer points below.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => setShowTeaching(true)}
              className="flex items-center justify-center gap-2 p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition shadow-lg"
            >
              <BookOpen className="w-5 h-5" />
              View Teaching
            </button>
            <button
              onClick={() => setShowPrayer(true)}
              className="flex items-center justify-center gap-2 p-4 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition shadow-lg"
            >
              <Heart className="w-5 h-5" />
              View Prayers
            </button>
          </div>

          {/* Score Cards (Only show if results revealed) */}
          {resultsRevealed && (
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* My Score */}
              <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-emerald-600">
                <h3 className="font-semibold text-gray-700 mb-4">Your Score</h3>
                {myCompleted && myScore !== undefined ? (
                  <>
                    <div className="flex items-end gap-2 mb-4">
                      <span className="text-4xl font-bold text-emerald-600">{myScore}</span>
                      <span className="text-xl text-gray-400 mb-1">/ {maxScore}</span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${
                          myInterpretation?.color === 'emerald' ? 'bg-emerald-500' :
                          myInterpretation?.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${(myScore / maxScore) * 100}%` }}
                      ></div>
                    </div>

                    {myInterpretation && (
                      <div className={`p-4 rounded-lg ${
                        myInterpretation.color === 'emerald' ? 'bg-emerald-50 border border-emerald-200' :
                        myInterpretation.color === 'amber' ? 'bg-amber-50 border border-amber-200' : 
                        'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{myInterpretation.icon}</span>
                          <span className={`font-bold ${
                            myInterpretation.color === 'emerald' ? 'text-emerald-700' :
                            myInterpretation.color === 'amber' ? 'text-amber-700' : 'text-red-700'
                          }`}>
                            {myInterpretation.level}
                          </span>
                        </div>
                        <p className={`text-sm ${
                          myInterpretation.color === 'emerald' ? 'text-emerald-600' :
                          myInterpretation.color === 'amber' ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {myInterpretation.message}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-gray-500">
                    <AlertCircle className="w-5 h-5" />
                    <span>Not completed yet</span>
                  </div>
                )}
              </div>

              {/* Partner Score */}
              <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-teal-600">
                <h3 className="font-semibold text-gray-700 mb-4">
                  {couple?.sharingEnabled ? "Partner's Score" : "Partner Status"}
                </h3>
                {partnerCompleted ? (
                  couple?.sharingEnabled && partnerScore !== undefined ? (
                    <>
                      <div className="flex items-end gap-2 mb-4">
                        <span className="text-4xl font-bold text-teal-600">{partnerScore}</span>
                        <span className="text-xl text-gray-400 mb-1">/ {maxScore}</span>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                        <div
                          className={`h-3 rounded-full transition-all duration-500 ${
                            partnerInterpretation?.color === 'emerald' ? 'bg-teal-500' :
                            partnerInterpretation?.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${(partnerScore / maxScore) * 100}%` }}
                        ></div>
                      </div>

                      {partnerInterpretation && (
                        <div className={`p-4 rounded-lg ${
                          partnerInterpretation.color === 'emerald' ? 'bg-teal-50 border border-teal-200' :
                          partnerInterpretation.color === 'amber' ? 'bg-amber-50 border border-amber-200' : 
                          'bg-red-50 border border-red-200'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">{partnerInterpretation.icon}</span>
                            <span className={`font-bold ${
                              partnerInterpretation.color === 'emerald' ? 'text-teal-700' :
                              partnerInterpretation.color === 'amber' ? 'text-amber-700' : 'text-red-700'
                            }`}>
                              {partnerInterpretation.level}
                            </span>
                          </div>
                          <p className={`text-sm ${
                            partnerInterpretation.color === 'emerald' ? 'text-teal-600' :
                            partnerInterpretation.color === 'amber' ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {partnerInterpretation.message}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-emerald-600">
                      <CheckCircle className="w-5 h-5" />
                      <span>Completed (sharing not enabled)</span>
                    </div>
                  )
                ) : (
                  <div className="flex items-center gap-2 text-gray-500">
                    <AlertCircle className="w-5 h-5" />
                    <span>Not completed yet</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pastoral Guidance (Only show if results revealed) */}
          {resultsRevealed && myInterpretation?.pastoralGuidance && (
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Pastoral Guidance
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {myInterpretation.pastoralGuidance}
              </p>
            </div>
          )}

          {/* Completion Status */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h3 className="font-bold text-gray-900 mb-4">Completion Status</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg ${myCompleted ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  {myCompleted ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-gray-400" />
                  )}
                  <span className={myCompleted ? 'text-emerald-700 font-medium' : 'text-gray-500'}>
                    You: {myCompleted ? 'Completed' : 'Pending'}
                  </span>
                </div>
              </div>
              <div className={`p-4 rounded-lg ${partnerCompleted ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  {partnerCompleted ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-gray-400" />
                  )}
                  <span className={partnerCompleted ? 'text-emerald-700 font-medium' : 'text-gray-500'}>
                    Partner: {partnerCompleted ? 'Completed' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>

            {bothCompleted && !resultsRevealed && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-600" />
                  <span className="text-blue-700 font-medium">
                    Both partners have completed! Waiting for counselor to reveal results.
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Navigation to Next/Previous Module */}
          <div className="flex flex-col sm:flex-row gap-4">
            {moduleNumber > 1 && (
              <button
                onClick={() => navigate(`/intensive/results/${moduleNumber - 1}`)}
                className="flex-1 py-3 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-medium transition flex items-center justify-center gap-2"
              >
                ← Module {moduleNumber - 1}
              </button>
            )}
            <button
              onClick={() => navigate('/couple/dashboard')}
              className="flex-1 py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition"
            >
              Back to Dashboard
            </button>
            {moduleNumber < 10 && (
              <button
                onClick={() => navigate(`/intensive/results/${moduleNumber + 1}`)}
                className="flex-1 py-3 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-medium transition flex items-center justify-center gap-2"
              >
                Module {moduleNumber + 1} →
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default IntensiveResultsPage;
